import React from "react";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { formatAmount } from "../../../PatientAccounts/utils/patientAccountUtils";
import { hasApplicableSectionTax } from "../utils/billingUtils";

const getDiscountLabel = (t, discountType, discountValue, discount, subtotal) => {
	if (discountType === "Percentage" && Number(discountValue) > 0) {
		return `${t("Discount")} (${Number(discountValue)}%)`;
	}
	if (discount > 0 && subtotal > 0) {
		const effectivePct = ((discount / subtotal) * 100).toFixed(2);
		return `${t("Discount")} (${effectivePct}%)`;
	}
	return t("Discount");
};

const TaxPercentInput = ({
	label,
	value,
	amount,
	onChange,
	money,
	disableScrollProps,
	disabled = false,
}) => (
	<Form.Group>
		<Form.Label className="billing-field-label mb-1">{label}</Form.Label>
		<InputGroup size="sm">
			<Form.Control
				type="number"
				min={0}
				max={100}
				step="0.01"
				value={value}
				disabled={disabled}
				onChange={(e) => {
					const raw = e.target.value;
					if (raw === "") {
						onChange(0);
						return;
					}
					const val = Number(raw);
					onChange(val >= 0 && val <= 100 ? val : 0);
				}}
				{...disableScrollProps}
			/>
			<InputGroup.Text>+{money(amount)}</InputGroup.Text>
		</InputGroup>
	</Form.Group>
);

const ReadOnlyTaxField = ({ label, value, amount, money }) => (
	<Form.Group>
		<Form.Label className="billing-field-label mb-1">{label}</Form.Label>
		<InputGroup size="sm">
			<Form.Control type="text" readOnly disabled value={value} className="billing-tax-readonly" />
			<InputGroup.Text>+{money(amount)}</InputGroup.Text>
		</InputGroup>
	</Form.Group>
);

export const BillingSectionAdjustments = ({
	t,
	subtotalLabel,
	sectionBilling,
	taxConfig,
	discountType,
	discountValue,
	onDiscountTypeChange,
	onDiscountValueChange,
	onTaxConfigChange,
	displayCurrency,
	getCurrencySymbol,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	disableScrollProps,
	showTax = true,
	disabledDiscount = false,
	disabledTax = false,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const money = (amount) => formatAmount(amount, displayCurrency, getCurrencySymbol);

	const {
		subtotal = 0,
		discount = 0,
		tax = 0,
		CGST = 0,
		SGST = 0,
		IGST = 0,
		VAT = 0,
	} = sectionBilling || {};

	const taxType = taxConfig?.taxType || posTaxType || "gst";
	const gstPercentage = taxConfig?.taxPercentage ?? 0;
	const cgstPercentage = taxConfig?.cgstPercentage ?? 0;
	const sgstPercentage = taxConfig?.sgstPercentage ?? 0;
	const vatPercentage = taxConfig?.taxPercentage ?? 0;

	return (
		<div className="billing-services-adjustments">
			{showTax && (
				<Row className="g-2 align-items-end mb-3">
					{taxType === "gst" && isSameState ? (
						<>
							<Col xs={12} sm={6} md={4}>
								<TaxPercentInput
									label={`${t("GST")} (%)`}
									value={gstPercentage}
									amount={tax}
									onChange={(val) => onTaxConfigChange({ taxPercentage: val })}
									money={money}
									disableScrollProps={disableScrollProps}
									disabled={disabledTax}
								/>
							</Col>
							<Col xs={12} sm={6} md={4}>
								<ReadOnlyTaxField
									label={`${t("CGST")} (%)`}
									value={cgstPercentage}
									amount={CGST}
									money={money}
								/>
							</Col>
							<Col xs={12} sm={6} md={4}>
								<ReadOnlyTaxField
									label={`${t("SGST")} (%)`}
									value={sgstPercentage}
									amount={SGST}
									money={money}
								/>
							</Col>
						</>
					) : taxType === "gst" ? (
						<Col xs={12} sm={6} md={4}>
							<TaxPercentInput
								label={`${t("GST")} (%)`}
								value={gstPercentage}
								amount={IGST}
								onChange={(val) => onTaxConfigChange({ taxPercentage: val })}
								money={money}
								disableScrollProps={disableScrollProps}
								disabled={disabledTax}
							/>
						</Col>
					) : (
						<Col xs={12} sm={6} md={4}>
							<TaxPercentInput
								label={`${t("VAT")} (%)`}
								value={vatPercentage}
								amount={VAT}
								onChange={(val) => onTaxConfigChange({ taxPercentage: val })}
								money={money}
								disableScrollProps={disableScrollProps}
								disabled={disabledTax}
							/>
						</Col>
					)}
				</Row>
			)}

			<Row className="g-2 align-items-end">
				<Col xs={12} sm={6} md={4}>
					<Form.Group>
						<Form.Label className="billing-field-label mb-1">
							{t("Discount Type")}
						</Form.Label>
						<Form.Select
							size="sm"
							value={discountType}
							disabled={disabledDiscount}
							onChange={(e) => onDiscountTypeChange(e.target.value)}
							{...disableScrollProps}
						>
							<option value="Fixed">{t("Flat")}</option>
							<option value="Percentage">{t("Percent (%)")}</option>
						</Form.Select>
					</Form.Group>
				</Col>
				<Col xs={12} sm={6} md={4}>
					<Form.Group>
						<Form.Label className="billing-field-label mb-1">
							{discountType === "Percentage"
								? t("Discount (%)")
								: t("Discount Amount")}
						</Form.Label>
						{discountType === "Percentage" ? (
							<InputGroup size="sm">
								<Form.Control
									type="number"
									min={0}
									max={100}
									value={discountValue}
									disabled={disabledDiscount}
									onChange={(e) => {
										const val = Number(e.target.value);
										onDiscountValueChange(
											val > 0 && val <= 100 ? val : 0
										);
									}}
									{...disableScrollProps}
								/>
								<InputGroup.Text>{money(discount)}</InputGroup.Text>
							</InputGroup>
						) : (
							<Form.Control
								type="number"
								size="sm"
								min={0}
								max={subtotal || undefined}
								value={discountValue}
								disabled={disabledDiscount}
								onChange={(e) => {
									const val = Number(e.target.value);
									onDiscountValueChange(
										val > 0 && val <= subtotal ? val : 0
									);
								}}
								{...disableScrollProps}
							/>
						)}
					</Form.Group>
				</Col>
				<Col xs={12} md={4}>
					<div className="billing-services-subtotal-pill">
						<span>{subtotalLabel}</span>
						<strong>{money(subtotal)}</strong>
					</div>
				</Col>
			</Row>
		</div>
	);
};

export const BillingSectionSummaryBreakdown = ({
	t,
	title,
	grandTotalLabel,
	summaryPrefix,
	billingSummary = {},
	taxConfig,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	displayCurrency,
	getCurrencySymbol,
	isOpen,
	onToggle,
	showTax = true,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const money = (amount) => formatAmount(amount, displayCurrency, getCurrencySymbol);

	const subtotal = billingSummary[`${summaryPrefix}_subtotal`] ?? 0;
	const discount = billingSummary[`${summaryPrefix}_discount`] ?? 0;
	const netTaxable = billingSummary[`${summaryPrefix}_net_taxable`] ?? 0;
	const tax = billingSummary[`${summaryPrefix}_tax`] ?? 0;
	const CGST = billingSummary[`${summaryPrefix}_CGST`] ?? 0;
	const SGST = billingSummary[`${summaryPrefix}_SGST`] ?? 0;
	const IGST = billingSummary[`${summaryPrefix}_IGST`] ?? 0;
	const VAT = billingSummary[`${summaryPrefix}_VAT`] ?? 0;
	const grandTotal = billingSummary[`${summaryPrefix}_total`] ?? 0;
	const discountType = billingSummary[`${summaryPrefix}_discount_type`] ?? "Percentage";
	const discountValue = billingSummary[`${summaryPrefix}_discount_value`] ?? 0;

	const discountLabel = getDiscountLabel(t, discountType, discountValue, discount, subtotal);
	const taxType = taxConfig?.taxType || posTaxType || "gst";
	const cgstPercentage = taxConfig?.cgstPercentage ?? 0;
	const sgstPercentage = taxConfig?.sgstPercentage ?? 0;
	const showTaxRows = showTax && (tax > 0 || hasApplicableSectionTax(taxConfig));

	return (
		<div className="billing-pharmacy-summary-block billing-services-summary-block mb-3">
			<button
				type="button"
				className="billing-pharmacy-summary-toggle"
				onClick={onToggle}
				aria-expanded={isOpen}
			>
				<span className="fw-semibold">{title}</span>
				<span className="billing-pharmacy-summary-toggle-meta">
					<span className="billing-pharmacy-summary-toggle-total">{money(grandTotal)}</span>
					<i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
				</span>
			</button>

			{isOpen && (
				<div className="billing-pharmacy-summary-body">
					<div className="summary-row">
						<span>{t("Total Taxable Value")}</span>
						<strong>{money(subtotal)}</strong>
					</div>
					{discount > 0 && (
						<>
							<div className="summary-row">
								<span>{discountLabel}</span>
								<strong className="text-danger">-{money(discount)}</strong>
							</div>
							<div className="summary-row">
								<span>{t("Net Taxable Value")}</span>
								<strong>{money(netTaxable)}</strong>
							</div>
						</>
					)}
					{showTaxRows && taxType === "gst" && isSameState && CGST > 0 && (
						<div className="summary-row">
							<span>
								{t("Add")}: CGST ({cgstPercentage}%)
							</span>
							<strong className="billing-tax-cell">+{money(CGST)}</strong>
						</div>
					)}
					{showTaxRows && taxType === "gst" && isSameState && SGST > 0 && (
						<div className="summary-row">
							<span>
								{t("Add")}: SGST ({sgstPercentage}%)
							</span>
							<strong className="billing-tax-cell">+{money(SGST)}</strong>
						</div>
					)}
					{showTaxRows && taxType === "gst" && !isSameState && IGST > 0 && (
						<div className="summary-row">
							<span>
								{t("Add")}: IGST ({taxConfig?.taxPercentage ?? 0}%)
							</span>
							<strong className="billing-tax-cell">+{money(IGST)}</strong>
						</div>
					)}
					{showTaxRows && taxType === "vat" && VAT > 0 && (
						<div className="summary-row">
							<span>
								{t("Add")}: VAT ({taxConfig?.taxPercentage ?? 0}%)
							</span>
							<strong className="billing-tax-cell">+{money(VAT)}</strong>
						</div>
					)}
					{showTaxRows && tax > 0 && (
						<div className="summary-row billing-pharmacy-tax-total">
							<span>{t("Total Tax")}</span>
							<strong className="billing-tax-cell">{money(tax)}</strong>
						</div>
					)}
					<div className="summary-row billing-pharmacy-grand-row">
						<span>{grandTotalLabel}</span>
						<strong>{money(grandTotal)}</strong>
					</div>
				</div>
			)}
		</div>
	);
};

export const BillingServicesAdjustments = (props) => (
	<BillingSectionAdjustments
		{...props}
		subtotalLabel={props.t("Services Subtotal")}
		sectionBilling={props.servicesBilling}
		taxConfig={props.servicesTaxConfig}
		discountType={props.servicesDiscountType}
		discountValue={props.servicesDiscountValue}
	/>
);

export const BillingServicesSummaryBreakdown = (props) => (
	<BillingSectionSummaryBreakdown
		{...props}
		title={props.title || props.t("Clinic Consultation & Services")}
		grandTotalLabel={props.grandTotalLabel || props.t("Services Grand Total")}
		summaryPrefix={props.summaryPrefix || "services"}
		taxConfig={props.servicesTaxConfig}
	/>
);

export const BillingInvestigationsAdjustments = (props) => (
	<BillingSectionAdjustments
		{...props}
		subtotalLabel={props.t("Lab Subtotal")}
		sectionBilling={props.investigationsBilling}
		taxConfig={props.investigationsTaxConfig}
		discountType={props.investigationsDiscountType}
		discountValue={props.investigationsDiscountValue}
	/>
);

export const BillingInvestigationsSummaryBreakdown = (props) => (
	<BillingSectionSummaryBreakdown
		{...props}
		title={props.t("Lab Investigations")}
		grandTotalLabel={props.t("Lab Grand Total")}
		summaryPrefix="investigation"
		taxConfig={props.investigationsTaxConfig}
	/>
);
